import { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useInView,
} from 'framer-motion';

const testimonials = [
  {
    name: 'Mrs. Uchenna Ogechi',
    role: 'Parent',
    image: '/images/Ogechi-Lilian.jpg',
    quote:
      'My children are products of the school and have made brilliant performances. They love the teachers so much.',
  },
  {
    name: 'Mr. Ojumonu Tony',
    role: 'Parent',
    image: '/images/Tony-Ojumonu.jpg',
    quote:
      "Exceptional experience. The teachers are truly interested in the children's educational progress.",
  },
  {
    name: 'Mr. Okorie',
    role: 'Parent',
    image: '/images/OKORIE.jpg',
    quote:
      'Not just a school but a home. An amazing experience for the past ten years. Simply the best.',
  },
  {
    name: 'Mr. Ajayi Bayo',
    role: 'Parent',
    image: '/images/AJAYI-BELLO.jpg',
    quote:
      'Made such a difference in children\'s lives. They have imparted Christian values and led a noble life.',
  },
  {
    name: 'Mrs. Chika Chinwe',
    role: 'Parent',
    image: '/images/CHIKA-CHINWE.png',
    quote:
      'Consistently cooperates with parents and students. They believe in positivity and excel in quality education.',
  },
  {
    name: 'Mrs. Ejikeme Mary',
    role: 'Parent',
    image: '/images/ejikeme-mary.jpg',
    quote:
      'Great school with amazing professional teachers. Their passion and dedication is praiseworthy.',
  },
];

/* ── Single card — entrance triggered by InView ── */
interface CardProps {
  t: typeof testimonials[number];
  index: number;
}

const TestimonialCard = ({ t, index }: CardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60, x: isEven ? -20 : 20 }}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{
        duration: 0.75,
        delay: (index % 3) * 0.13,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group"
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="relative h-full bg-white/5 backdrop-blur-md rounded-2xl p-6 sm:p-7 border border-white/8 hover:border-primary/30 hover:bg-white/8 transition-colors flex flex-col overflow-hidden"
      >
        {/* Hover accent blob */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

        {/* Large decorative quote mark */}
        <div
          aria-hidden
          className="absolute top-6 right-7 font-black text-[7rem] leading-none text-white/5 select-none"
        >
          "
        </div>

        {/* Avatar + name */}
        <div className="flex items-center gap-4 mb-6 relative z-10">
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{
              duration: 5 + index * 0.4,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="relative flex-shrink-0"
          >
            <div className="absolute inset-0 bg-primary/30 blur-lg rounded-full" />
            <img
              src={t.image}
              alt={t.name}
              className="w-14 h-14 rounded-2xl object-cover relative z-10 border-2 border-white/15"
              loading="lazy"
            />
          </motion.div>
          <div>
            <p className="font-black text-white text-base tracking-tight leading-tight">{t.name}</p>
            <p className="text-primary/70 text-[10px] font-bold tracking-widest uppercase mt-0.5">
              {t.role}
            </p>
          </div>
        </div>

        {/* Quote */}
        <blockquote className="text-white/60 font-medium leading-relaxed italic text-[15px] flex-grow relative z-10">
          "{t.quote}"
        </blockquote>
      </motion.div>
    </motion.div>
  );
};

/* ── Section heading ── */
const Heading = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="text-center mb-20"
    >
      <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">
        Family Voices
      </p>
      <h2 className="font-display font-black text-[clamp(2.5rem,6vw,4.5rem)] text-white tracking-tighter leading-[0.9] mb-6">
        What Parents <span className="text-primary">Say</span>
      </h2>
      <p className="text-slate-300 text-lg font-medium max-w-xl mx-auto leading-relaxed">
        Hear from families who have trusted our playful path to excellence.
      </p>
    </motion.div>
  );
};

/* ── Parallax background image strip ── */
const ParallaxBg = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.2] will-change-transform">
        <img
          src="/lovable-uploads/b16fa090-be13-4f85-8efe-34cce7ec0315.png"
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-[0.07]"
          loading="lazy"
        />
      </motion.div>
    </div>
  );
};

const Testimonials = () => (
  <section className="relative py-16 sm:py-28 md:py-40 bg-[#07091a] overflow-hidden">
    <ParallaxBg />

    {/* Ambient blobs */}
    <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
    <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[100px] pointer-events-none" />

    <div className="container mx-auto px-6 relative z-10">
      <Heading />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
        {testimonials.map((t, i) => (
          <TestimonialCard key={i} t={t} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Testimonials;
